import {
  Grid,
  Menu,
  MenuOption,
  MenuVariant,
  theme
} from '@focaldata/cin-ui-components'
import { useState } from 'react'
import CompleteChartList from '../../../../controls/CompleteChartList'
import { useCombinedChartListData } from '../../../../controls/CompleteChartList/CompleteChartList.hooks'
import { Wave } from '../../../../data/gql-gen/dashboard/graphql'
import { SelectedFilter } from '../../../../data/model/results'
import { useProjectId } from '../../../../hooks/useProjectId'
import ResultsPageLayout from '../../../../layouts/ResultsPageLayout'
import { useGetProjectByProjectId } from '../../../../modules/Project/Project.hooks'
import RequestCombinedRawData from '../../../../modules/Project/RequestRawData/RequestCombinedRawData'
import { TablePagination } from '../../Survey/Results/DefaultMuiTablePagination'
import { getMasterSurvey, surveysByCreation } from '../markets.utils'
import { CombinedAnalyticsResultsFilters } from './CombinedAnalyticsResultsFilters'
import { CombinedExportMenu } from './CombinedExportMenu'

const ITEMS_PER_PAGE = 50

// The language the shared questionnaire is authored in, and the only one this
// view reads. Same constant as the Translations, Preview and docx-export
// surfaces.
const MASTER_LANGUAGE = 'en'

/**
 * Combined multi-market results: the charts pooled across all of a project's
 * market surveys, fetched from fd-core-responses' combined endpoint by project
 * id (and an explicit waveId for trackers). The response is the same shape as
 * the single-market results, with a synthetic market (country) question
 * prepended, so it renders through the same
 * CompleteChartList as the per-market view.
 *
 * The per-market options (pause/end a market, market-scoped data downloads)
 * are intentionally omitted: those actions are survey-scoped. The project-level
 * exports are: the pooled raw data via "Export data" (getCombinedRawData ->
 * POST /artifacts/multimarket/{projectId}/download) and the shared
 * questionnaire as .docx via the kebab (CombinedExportMenu).
 *
 * The filter panel (CombinedAnalyticsResultsFilters) is sourced from the
 * project-scoped filters endpoint and feeds the same `selectedFilters` state
 * into the combined results query below, mirroring how Results.tsx wires
 * AnalyticsResultsFilters into useCompleteChartListData for a single survey.
 *
 * Unlike that per-market view, this one is English-only and has no language
 * selector. Pooling every market, it reads as the shared questionnaire rather
 * than as any one market's fieldwork — so it asks for the language the
 * questionnaire is authored in, not the dominant allocation of whichever market
 * happens to lead the list. Nobody has asked to read the pooled charts in a
 * market language; if that changes, useResultsLanguages still supplies the
 * per-market codes.
 */
export const CombinedResults = ({ wave }: { wave?: Wave }) => {
  const projectId = useProjectId()
  const [selectedFilters, setSelectedFilters] = useState<SelectedFilter[]>([])
  const [rowsPerPage, setRowsPerPage] = useState(ITEMS_PER_PAGE)
  const [page, setPage] = useState(0)
  const [isRawDataDialogOpen, setIsRawDataDialogOpen] = useState(false)

  const { project } = useGetProjectByProjectId()
  const masterSurveyId = wave
    ? surveysByCreation(wave.surveys)[0]?.surveyId
    : getMasterSurvey(project)?.surveyId

  const { chartData, totalCount, summaryDataLoading } =
    useCombinedChartListData(projectId, selectedFilters, page, rowsPerPage, {
      language: MASTER_LANGUAGE,
      waveId: wave?.waveId
    })

  const exportMenuOptions: MenuOption[] = [
    {
      id: 0,
      options: [
        {
          text: 'Download raw data file',
          onClick: () => setIsRawDataDialogOpen(true)
        }
      ]
    }
  ]

  const paginationControl = (
    <TablePagination
      // A -1 count tells MUI TablePagination the total is unknown: it shows
      // "X-Y" with no total and (via showLastButton) hides the last-page button.
      count={totalCount}
      labelRowsPerPage="Charts per page"
      page={page}
      onPageChange={(e, newPage) => setPage(newPage)}
      rowsPerPage={rowsPerPage}
      onRowsPerPageChange={(e) => {
        setRowsPerPage(Number(e.target.value))
        setPage(0)
      }}
      showFirstButton
      showLastButton={totalCount !== -1}
    />
  )

  return (
    <ResultsPageLayout
      isLoading={summaryDataLoading}
      optionsBar={
        <Grid container justifyContent="flex-end" alignItems="center">
          <Grid
            container
            item
            xs="auto"
            justifyContent="flex-end"
            alignItems="center"
          >
            {/* Same padding as the per-market bar's Export data cell
                (controls/ResultsOptions styles.requestData), so the kebab isn't
                flush against the button. */}
            <Grid item xs="auto" style={{ padding: theme.spacing(0, 1, 0, 2) }}>
              <Menu
                variant={MenuVariant.Body}
                menuOptions={exportMenuOptions}
                title="Export data"
              />
            </Grid>
            <CombinedExportMenu
              masterSurveyId={masterSurveyId}
              language={MASTER_LANGUAGE}
            />
            <RequestCombinedRawData
              waveId={wave?.waveId}
              isDialogOpen={isRawDataDialogOpen}
              setIsDialogOpen={setIsRawDataDialogOpen}
            />
          </Grid>
        </Grid>
      }
      resultCards={
        <>
          {paginationControl}
          <CompleteChartList
            chartData={chartData}
            isLoading={summaryDataLoading}
            showAudienceCriteriaQuestions
          />
          {paginationControl}
        </>
      }
      filters={
        <CombinedAnalyticsResultsFilters
          waveId={wave?.waveId}
          selectedFilters={selectedFilters}
          setSelectedFilters={setSelectedFilters}
          language={MASTER_LANGUAGE}
        />
      }
    />
  )
}
